import { Component, Output, EventEmitter, HostListener, OnInit } from '@angular/core';
import { timeout, finalize } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { NotesService, Note as ApiNote } from '../../services/notes.service';
import { AuthService } from '../../services/auth.service';
import { ThemeService } from "../../theme.service";
import { environment } from '../../../environments/environment';

interface Note {
  id: string;
  topic_id?: string;
  title: string;
  description: string;
  time: string;
  color: string;
  showMenu?: boolean;
}

@Component({
  selector: 'app-notes-sidebar',
  templateUrl: './notes-sidebar.component.html',
  styleUrls: ['./notes-sidebar.component.css']
})
export class NotesSidebarComponent implements OnInit {
  readonly enableStudentThemes = environment.enableStudentThemes ?? false;
  @Output() closeSidebar = new EventEmitter<void>();

  notes: Note[] = [];
  user: any;
  userId: string = '';
  isLoading: boolean = false;

  isEditorOpen = false;
  currentNote: any = { title: '', content: '' };
  showRenameModal = false;
  showDeleteModal = false;
  noteToEdit: Note | null = null;
  renameInput: string = '';

  constructor(
    private notesService: NotesService,
    private authService: AuthService,
      private themeService: ThemeService
  ) { }

  ngOnInit(): void {
    this.user = this.authService.getUser();
    this.userId = this.user?.user?.id || this.user?.user?._id;
    
    if (this.userId) {
      this.loadNotes();
    }
  }

  loadNotes(): void {
    this.isLoading = true;
    this.notesService.getNotes(this.userId).pipe(
      timeout(15000),
      finalize(() => { this.isLoading = false; })
    ).subscribe({
      next: (response) => {
        // Handle API response: direct array, or wrapped { data: [] } / { topics: [] }
        const notesArray = Array.isArray(response)
          ? response
          : (response as any)?.data ?? (response as any)?.topics ?? [];
        this.notes = notesArray.map((note: any) => ({
          id: note._id || '',
          topic_id: note._id,
          title: note.title,
          description: note.description,
          time: this.getTimeAgo(note.updated_at || note.created_at || ''),
          color: note.color || this.getRandomColor(),
          showMenu: false
        }));
      },
      error: (error) => {
        console.error('Error loading notes:', error);
        this.notes = [];
        const isTimeout = error?.name === 'TimeoutError' || error?.message?.toLowerCase?.().includes('timeout');
        Swal.fire({
          icon: 'error',
          title: isTimeout ? 'Request timed out' : 'Error',
          text: isTimeout ? 'Could not load notes. Please check your connection and try again.' : 'Failed to load notes. Please try again.',
          timer: 2000,
          showConfirmButton: false
        });
      }
    });
  }

  getTimeAgo(timestamp: string): string {
    if (!timestamp) return 'Just now';
    
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}mins ago`;
    if (diffHours < 24) return `${diffHours}hrs ago`;
    if (diffDays < 7) return `${diffDays}days ago`;
    return past.toLocaleDateString();
  }

  getRandomColor(): string {
    const colors = ['#e6e0d4', '#ffd4d4', '#d4e4ff', '#d4ffd4', '#ffe4d4'];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  openEditor() {
    this.isEditorOpen = true;
    this.currentNote = { title: '', content: '' }; // Reset or load
  }

  closeEditor() {
    this.isEditorOpen = false;
  }

  saveNote() {
    console.log('Saving note:', this.currentNote);
    
    if (!this.currentNote.title || !this.currentNote.title.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Title Required',
        text: 'Please enter a title for the note.',
        timer: 2000,
        showConfirmButton: false
      });
      return;
    }

    const noteData = {
      user_id: this.userId,
      title: this.currentNote.title.trim(),
      description: this.currentNote.content || ''
    };

    if (this.currentNote.id) {
      // Update existing note
      const updateData = {
        ...noteData,
        topic_id: this.currentNote.topic_id || this.currentNote.id
      };

      this.notesService.updateNote(updateData).subscribe({
        next: (response) => {
          Swal.fire({
            icon: 'success',
            title: 'Updated!',
            text: response.message || 'Note has been updated successfully.',
            timer: 2000,
            showConfirmButton: false
          });
          this.loadNotes(); // Reload notes
          this.closeEditor();
        },
        error: (error) => {
          console.error('Error updating note:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.error?.message || 'Failed to update note. Please try again.',
            showConfirmButton: true
          });
        }
      });
    } else {
      // Create new note
      this.notesService.addNote(noteData).subscribe({
        next: (response) => {
          Swal.fire({
            icon: 'success',
            title: 'Created!',
            text: response.message || 'Note has been created successfully.',
            timer: 2000,
            showConfirmButton: false
          });
          this.loadNotes(); // Reload notes
          this.closeEditor();
        },
        error: (error) => {
          console.error('Error creating note:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.error?.message || 'Failed to create note. Please try again.',
            showConfirmButton: true
          });
        }
      });
    }
  }

  close() {
    this.closeSidebar.emit();
  }

  openEditorWithNote(note: Note, event: MouseEvent): void {
    // Only open editor if not clicking on the 3-dots button or its menu
    const target = event.target as HTMLElement;
    if (!target.closest('.menu-wrapper') && !target.closest('.dropdown-tooltip')) {
      this.currentNote = { 
        id: note.id,
        topic_id: note.topic_id || note.id,
        title: note.title, 
        content: note.description || ''
      };
      this.isEditorOpen = true;
      // Close any open menus
      this.notes.forEach(n => n.showMenu = false);
    }
  }

  toggleMenu(noteId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.notes.forEach(note => {
      note.showMenu = note.id === noteId ? !note.showMenu : false;
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.menu-wrapper') && !target.closest('.dropdown-tooltip')) {
      this.notes.forEach(note => note.showMenu = false);
    }
  }

  openModal(type: 'rename' | 'delete', note: Note, event: MouseEvent) {
    event.stopPropagation();
    this.noteToEdit = note;

    if (type === 'rename') {
      this.renameInput = note.title || '';
      this.showRenameModal = true;
    } else if (type === 'delete') {
      this.showDeleteModal = true;
    }
    
    // Close the menu
    note.showMenu = false;
  }

  closeModal() {
    this.showRenameModal = false;
    this.showDeleteModal = false;
    this.noteToEdit = null;
    this.renameInput = '';
  }

  confirmRename() {
    if (this.noteToEdit && this.renameInput.trim()) {
      const updateData = {
        user_id: this.userId,
        topic_id: this.noteToEdit.topic_id || this.noteToEdit.id,
        title: this.renameInput.trim(),
        description: this.noteToEdit.description || ''
      };

      this.notesService.updateNote(updateData).subscribe({
        next: (response) => {
          Swal.fire({
            icon: 'success',
            title: 'Renamed!',
            text: response.message || 'Note has been renamed successfully.',
            timer: 2000,
            showConfirmButton: false
          });
          this.loadNotes(); // Reload notes
          this.closeModal();
        },
        error: (error) => {
          console.error('Error renaming note:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.error?.message || 'Failed to rename note. Please try again.',
            showConfirmButton: true
          });
          this.closeModal();
        }
      });
    }
  }

  confirmDelete() {
    if (this.noteToEdit) {
      const topicId = this.noteToEdit.topic_id || this.noteToEdit.id;
      
      this.notesService.deleteNote(this.userId, topicId).subscribe({
        next: (response) => {
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: response.message || 'Note has been deleted successfully.',
            timer: 2000,
            showConfirmButton: false
          });
          this.loadNotes(); // Reload notes
          this.closeModal();
        },
        error: (error) => {
          console.error('Error deleting note:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.error?.message || 'Failed to delete note. Please try again.',
            showConfirmButton: true
          });
          this.closeModal();
        }
      });
    } else {
      this.closeModal();
    }
  }

  editNote(note: Note, event: MouseEvent) {
    event.stopPropagation();
    this.currentNote = { 
      id: note.id,
      topic_id: note.topic_id || note.id,
      title: note.title, 
      content: note.description || ''
    };
    this.isEditorOpen = true;
    note.showMenu = false;
  }
    isDarkTheme(): boolean {
    return this.themeService.getCurrentTheme();
  }
}
